import React from 'react';
import type { ShiftLedgerSummary, ShiftRegisterItem } from '@/api/models';
import { useDeviceRosterQuery } from '@/api/queries/auth';
import {
    useShiftLedgerSummaryQuery,
    useShiftRegisterQuery,
} from '@/api/queries/shifts';
import type { RefreshState, SectionState } from '@/utils';
import { ALL_SELLERS_KEY, sortOpenFirst, todayIso } from '../../shiftsMeta';
import { useShiftClose, type ShiftCloseLogic } from './useShiftClose';

export type ShiftSellerOption = { id: string; name: string };

/** The summary always carries the shift's own figures, so it is never empty. */
export type ShiftSummaryState = Exclude<
    SectionState<ShiftLedgerSummary>,
    { status: 'empty' }
>;

export type ShiftRegisterFilters = {
    date: string;
    sellerId: string | null;
    sellers: ShiftSellerOption[];
    onDateChange: (date: string) => void;
    onSellerChange: (sellerId: string | null) => void;
};

export type ShiftSummaryLogic = {
    isOpen: boolean;
    state: ShiftSummaryState;
    onOpen: (shiftId: string) => void;
    onClose: () => void;
};

export type ShiftRegisterLogic = {
    filters: ShiftRegisterFilters;
    list: SectionState<ShiftRegisterItem[]>;
    summary: ShiftSummaryLogic;
    /** Closing a stuck open shift from its row (SEL-31). */
    close: ShiftCloseLogic;
};

const refreshOf = (isFetching: boolean, isError: boolean): RefreshState =>
    isFetching ? 'pending' : isError ? 'error' : 'idle';

/**
 * The admin Shifts page's state: the day and seller filters, the branch's shift
 * register for them (open shifts first), and the ledger summary of the shift
 * the admin opened, and the close of a stuck open shift. The server scopes every
 * read and the close to the caller's branch.
 *
 * `locale` is the page language; it picks the account-name translation in the
 * summary.
 */
export const useShiftRegister = (locale: string): ShiftRegisterLogic => {
    const [date, setDate] = React.useState<string>(todayIso);
    const [sellerId, setSellerId] = React.useState<string | null>(null);
    const [openShiftId, setOpenShiftId] = React.useState<string | null>(null);

    const {
        data: registerPage,
        isError: isRegisterError,
        isFetching: isRegisterFetching,
        refetch: refetchRegister,
    } = useShiftRegisterQuery({ date, sellerId });
    const { data: roster } = useDeviceRosterQuery();
    const close = useShiftClose();
    const {
        data: ledger,
        isError: isLedgerError,
        isFetching: isLedgerFetching,
        refetch: refetchLedger,
    } = useShiftLedgerSummaryQuery(openShiftId, locale);

    // The seller filter lists the branch's sellers. Without a roster the filter
    // just offers "all sellers"; the register itself does not depend on it.
    const sellers = React.useMemo<ShiftSellerOption[]>(
        () =>
            roster?.success
                ? roster.response.items
                      .filter((user) => user.role === 'Seller')
                      .map(({ id, name }) => ({ id, name }))
                : [],
        [roster],
    );

    const onRetryList = React.useCallback(() => {
        void refetchRegister();
    }, [refetchRegister]);

    const list = React.useMemo<SectionState<ShiftRegisterItem[]>>(() => {
        if (registerPage === undefined) {
            return isRegisterError
                ? { status: 'error', onRetry: onRetryList }
                : { status: 'loading' };
        }
        const refresh = refreshOf(isRegisterFetching, isRegisterError);
        return registerPage.items.length === 0
            ? { status: 'empty', refresh, onRetry: onRetryList }
            : {
                  status: 'ready',
                  data: sortOpenFirst(registerPage.items),
                  refresh,
                  onRetry: onRetryList,
              };
    }, [registerPage, isRegisterError, isRegisterFetching, onRetryList]);

    const onRetrySummary = React.useCallback(() => {
        void refetchLedger();
    }, [refetchLedger]);

    const summaryState = React.useMemo<ShiftSummaryState>(() => {
        if (ledger === undefined) {
            return isLedgerError
                ? { status: 'error', onRetry: onRetrySummary }
                : { status: 'loading' };
        }
        return {
            status: 'ready',
            data: ledger,
            refresh: refreshOf(isLedgerFetching, isLedgerError),
            onRetry: onRetrySummary,
        };
    }, [ledger, isLedgerError, isLedgerFetching, onRetrySummary]);

    // A cleared picker is not a day: keep the last one rather than read nothing.
    const onDateChange = React.useCallback((next: string) => {
        if (next) {
            setDate(next);
        }
    }, []);

    // The picker emits its "all sellers" option as a key; that means no filter.
    const onSellerChange = React.useCallback((next: string | null) => {
        setSellerId(next === null || next === ALL_SELLERS_KEY ? null : next);
    }, []);

    const onOpen = React.useCallback((shiftId: string) => {
        setOpenShiftId(shiftId);
    }, []);

    const onClose = React.useCallback(() => {
        setOpenShiftId(null);
    }, []);

    return {
        filters: { date, sellerId, sellers, onDateChange, onSellerChange },
        list,
        summary: {
            isOpen: openShiftId !== null,
            state: summaryState,
            onOpen,
            onClose,
        },
        close,
    };
};
